import { createReducer } from 'reduxsauce'
import ActionTypes from '../actions/types'

const INITIAL_STATE = {
  id: '',
  name: '',
  owner: '',
  subscriptionData: {},
  initialSetupMarkers: {},
  subscriptionKeys: [],
  members: [],
  teams: [],
  rotations: [],
  integrationConfigs: [],
  alerts: [],
  subscription: null,
  product: null,
  incidentPrefixes: [],
  incidentFilters: {},
  labels: [],
  apiVersion: 'v1'
}

const setAccountInfo = (state, action) => {
  const {
    id = '',
    name = '',
    owner = '',
    subscriptionData = {},
    initialSetupMarkers,
    incidentPrefixes = [],
    escalationIntervals = [],
    filterValues = {},
    labels = [],
    apiVersion = 'v1'
  } = action.payload.accountInfo

  return {
    ...state,
    id,
    name,
    owner,
    subscriptionData,
    initialSetupMarkers,
    incidentPrefixes,
    escalationIntervals,
    filterValues,
    labels,
    apiVersion
  }
}

const setMembers = (state, action) => ({
  ...state,
  members: action.payload.members
})

const addMember = (state, action) => {
  const newMember = action.payload.member
  const existingMemberIndex = state.members.findIndex(
    member => member.id === newMember.id
  )

  if (existingMemberIndex !== -1) {
    // Replace existing member
    const newMembers = state.members.map(member =>
      member.id === newMember.id ? newMember : member
    )
    return {
      ...state,
      members: newMembers
    }
  } else {
    // Add new member
    return {
      ...state,
      members: [...state.members, newMember]
    }
  }
}

const editMember = (state, action) => {
  const editedMember = action.payload.member
  const newMembers = state.members.map(member =>
    member.id === editedMember.id ? editedMember : member
  )

  return {
    ...state,
    members: newMembers
  }
}

const removeMember = (state, action) => {
  const removedMember = action.payload.member
  const newMembers = state.members.filter(
    member => member.id !== removedMember.id
  )

  return {
    ...state,
    members: newMembers
  }
}

const addTeam = (state, action) => {
  const newTeam = action.payload.team
  const existingTeamIndex = state.teams.findIndex(
    team => team.id === newTeam.id
  )

  if (existingTeamIndex !== -1) {
    // Replace existing team
    const newTeams = state.teams.map(team =>
      team.id === newTeam.id ? newTeam : team
    )
    return {
      ...state,
      teams: newTeams
    }
  } else {
    // Add new team
    return {
      ...state,
      teams: [...state.teams, newTeam]
    }
  }
}

const editTeam = (state, action) => {
  const editedTeam = action.payload.team
  const newTeams = state.teams.map(team =>
    team.id === editedTeam.id ? editedTeam : team
  )

  return {
    ...state,
    teams: newTeams
  }
}

const removeTeam = (state, action) => {
  const removedTeam = action.payload.team
  const newTeams = state.teams.filter(team => team.id !== removedTeam.id)

  return {
    ...state,
    teams: newTeams
  }
}

const addRotation = (state, action) => {
  const newRotation = action.payload.rotation
  const existingRotationIndex = state.rotations.findIndex(
    rotation => rotation.id === newRotation.id
  )

  if (existingRotationIndex !== -1) {
    // Replace existing rotation
    const newRotations = state.rotations.map(rotation =>
      rotation.id === newRotation.id ? newRotation : rotation
    )
    return {
      ...state,
      rotations: newRotations
    }
  } else {
    // Add new rotation
    return {
      ...state,
      rotations: [...state.rotations, newRotation]
    }
  }
}

const editRotation = (state, action) => {
  const editedRotation = action.payload.rotation
  const newRotations = state.rotations.map(rotation =>
    rotation.id === editedRotation.id ? editedRotation : rotation
  )

  return {
    ...state,
    rotations: newRotations
  }
}

const removeRotation = (state, action) => {
  const removedRotation = action.payload.rotation
  const newRotations = state.rotations.filter(
    rotation => rotation.id !== removedRotation.id
  )

  return {
    ...state,
    rotations: newRotations
  }
}

const addSubscriptionKey = (state, action) => {
  const newSubscriptionKey = action.payload.subscriptionKey
  const existingSubscriptionKeyIndex = state.subscriptionKeys.findIndex(
    subscriptionKey => subscriptionKey.id === newSubscriptionKey.id
  )

  if (existingSubscriptionKeyIndex !== -1) {
    // Replace existing subscription key
    const newSubscriptionKeys = state.subscriptionKeys.map(subscriptionKey =>
      subscriptionKey.id === newSubscriptionKey.id
        ? newSubscriptionKey
        : subscriptionKey
    )
    return {
      ...state,
      subscriptionKeys: newSubscriptionKeys
    }
  } else {
    // Add new subscription key
    return {
      ...state,
      subscriptionKeys: [...state.subscriptionKeys, newSubscriptionKey]
    }
  }
}

const editSubscriptionKey = (state, action) => {
  const editedSubscriptionKey = action.payload.subscriptionKey
  const newSubscriptionKeys = state.subscriptionKeys.map(subscriptionKey =>
    subscriptionKey.id === editedSubscriptionKey.id
      ? editedSubscriptionKey
      : subscriptionKey
  )

  return {
    ...state,
    subscriptionKeys: newSubscriptionKeys
  }
}

const removeSubscriptionKey = (state, action) => {
  const removedSubscriptionKey = action.payload.subscriptionKey
  const newSubscriptionKeys = state.subscriptionKeys.filter(
    subscriptionKey => subscriptionKey.id !== removedSubscriptionKey.id
  )

  return {
    ...state,
    subscriptionKeys: newSubscriptionKeys
  }
}

const addAlert = (state, action) => {
  const newAlert = action.payload.alert
  const existingAlertIndex = state.alerts.findIndex(
    alert => alert.id === newAlert.id
  )

  if (existingAlertIndex !== -1) {
    // Replace existing alert
    const newAlerts = state.alerts.map(alert =>
      alert.id === newAlert.id ? newAlert : alert
    )
    return {
      ...state,
      alerts: newAlerts
    }
  } else {
    // Add new alert
    return {
      ...state,
      alerts: [...state.alerts, newAlert]
    }
  }
}

const editAlert = (state, action) => {
  const editedAlert = action.payload.alert
  const newAlerts = state.alerts.map(alert =>
    alert.id === editedAlert.id ? editedAlert : alert
  )

  return {
    ...state,
    alerts: newAlerts
  }
}

const removeAlert = (state, action) => {
  const removedAlert = action.payload.alert
  const newAlerts = state.alerts.filter(alert => alert.id !== removedAlert.id)

  return {
    ...state,
    alerts: newAlerts
  }
}

const setTeams = (state, action) => ({
  ...state,
  teams: action.payload.teams
})

const setAlerts = (state, action) => ({
  ...state,
  alerts: action.payload.alerts
})

const setSubscriptionKeys = (state, action) => ({
  ...state,
  subscriptionKeys: action.payload.subscriptionKeys
})

const setRotations = (state, action) => ({
  ...state,
  rotations: action.payload.rotations
})

const setIntegrationConfigs = (state, action) => ({
  ...state,
  integrationConfigs: action.payload.integrationConfigs
})

const setSubscription = (state, action) => ({
  ...state,
  subscription: action.payload.subscription
})

const setProduct = (state, action) => ({
  ...state,
  product: action.payload.product
})

const setIncidentFilters = (state, action) => ({
  ...state,
  incidentFilters: action.payload.filters
})

const setLabels = (state, action) => ({
  ...state,
  labels: action.payload.labels
})

const ACTION_HANDLERS = {
  [ActionTypes.SET_ACCOUNT_INFO]: setAccountInfo,
  [ActionTypes.SET_MEMBERS]: setMembers,
  [ActionTypes.ADD_MEMBER]: addMember,
  [ActionTypes.EDIT_MEMBER]: editMember,
  [ActionTypes.REMOVE_MEMBER]: removeMember,
  [ActionTypes.ADD_TEAM]: addTeam,
  [ActionTypes.EDIT_TEAM]: editTeam,
  [ActionTypes.REMOVE_TEAM]: removeTeam,
  [ActionTypes.ADD_ROTATION]: addRotation,
  [ActionTypes.EDIT_ROTATION]: editRotation,
  [ActionTypes.REMOVE_ROTATION]: removeRotation,
  [ActionTypes.ADD_ALERT]: addAlert,
  [ActionTypes.EDIT_ALERT]: editAlert,
  [ActionTypes.REMOVE_ALERT]: removeAlert,
  [ActionTypes.ADD_SUBSCRIPTION_KEY]: addSubscriptionKey,
  [ActionTypes.EDIT_SUBSCRIPTION_KEY]: editSubscriptionKey,
  [ActionTypes.REMOVE_SUBSCRIPTION_KEY]: removeSubscriptionKey,
  [ActionTypes.SET_TEAMS]: setTeams,
  [ActionTypes.SET_ALERTS]: setAlerts,
  [ActionTypes.SET_SUBSCRIPTION_KEYS]: setSubscriptionKeys,
  [ActionTypes.SET_ROTATIONS]: setRotations,
  [ActionTypes.SET_INTEGRATION_CONFIGS]: setIntegrationConfigs,
  [ActionTypes.SET_SUBSCRIPTION]: setSubscription,
  [ActionTypes.SET_PRODUCT]: setProduct,
  [ActionTypes.SET_INCIDENT_FILTERS]: setIncidentFilters,
  [ActionTypes.SET_LABELS]: setLabels
}

export default createReducer(INITIAL_STATE, ACTION_HANDLERS)
